/**
 * VAR Explorer Component
 *
 * Values at Risk Explorer using ConfigurableBaseExplorer
 * Issue: #289
 */

import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import ConfigurableBaseExplorer from '../baseExplorer/ConfigurableBaseExplorer';
import ContextHelp from '../../../easymap/ContextHelp/ContextHelp';
import { createVARConfig, VAREntityEntry } from './varConfig';
import { useFusionAuth } from '@fusionauth/react-sdk';
import VARFlagDialog, { FlagType } from './VARFlagDialog';
import { usePermissions } from '../../../context/PermissionContext';
import type { VAREntity } from '../../../types/var';
import './VARExplorer.scss';

interface VARExplorerProps {
  initialFilters?: Record<string, unknown>;
  initialVarId?: string;
  height?: number;
  width?: number;
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  windowId?: string;
  onSelectionChange?: (selection: VAREntityEntry[]) => void;
  onDataChange?: () => void;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
}

/**
 * VAR Explorer Component
 *
 * Displays Values at Risk data using the ConfigurableBaseExplorer pattern.
 * Supports pagination, filtering, and sorting.
 */
export const VARExplorer: React.FC<VARExplorerProps> = ({
  initialFilters = {},
  initialVarId,
  height = window.innerWidth <= 768 ? window.innerHeight - 20 : 600,
  width = window.innerWidth <= 768 ? window.innerWidth - 20 : 900,
  onClose,
  className,
  style,
  windowId,
  onSelectionChange,
  onDataChange,
  initialPosition,
  initialSize
}) => {
  const { isLoggedIn } = useFusionAuth();
  const { canDo } = usePermissions();
  const canFlagRed = canDo('var', 'flag_red');
  const canFlagSprinkler = canDo('var', 'flag_sprinkler');

  // Imperative refs for window and search control
  const minimizeRef = useRef<(() => void) | null>(null);
  const restoreRef = useRef<(() => void) | null>(null);
  const searchRef = useRef<((term: string) => void) | null>(null);

  // Flag dialog state
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagDialogEntity, setFlagDialogEntity] = useState<VAREntity | null>(null);
  const [flagDialogType, setFlagDialogType] = useState<FlagType>('redFlag');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const openFlagDialog = useCallback((entity: VAREntityEntry, flagType: FlagType) => {
    setFlagDialogEntity(entity as unknown as VAREntity);
    setFlagDialogType(flagType);
    setFlagDialogOpen(true);
  }, []);

  // Restore the window and filter to the VAR when "View in Explorer" is triggered
  useEffect(() => {
    const handler = (e: Event) => {
      const { varId } = (e as CustomEvent).detail || {};
      restoreRef.current?.();
      if (varId) {
        // Small delay to let the restore render complete before searching
        setTimeout(() => searchRef.current?.(varId), 50);
      }
    };
    window.addEventListener('openVarExplorer', handler);
    return () => window.removeEventListener('openVarExplorer', handler);
  }, []);

  const handleViewOnMap = useCallback((entity: VAREntityEntry) => {
    if (!entity.location) return;
    // Pass the full entity so the layer can pop up archived VARs (which
    // aren't in the layer's Active-only lookup) and VARs of any status when
    // the layer is disabled.
    window.dispatchEvent(new CustomEvent('intellifire:var-view-on-map', {
      detail: { entityId: entity.id, entity }
    }));
    minimizeRef.current?.();
  }, []);

  const handleFlagSuccess = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
    onDataChange?.();
  }, [onDataChange]);

  // Create the configuration with custom actions for flag toggling
  const config = useMemo(() => {
    const baseConfig = createVARConfig();
    return {
      ...baseConfig,
      features: {
        ...baseConfig.features,
        customActions: {
          // Per-button gating below via isVisible/isEnabled — keeps the
          // re-memo dependency list tight (only re-runs when grants change).
          enabled: true,
          actions: [
            {
              id: 'toggleRedFlag',
              label: 'Red Flag',
              icon: '<i class="fa-solid fa-flag"></i>',
              className: 'btn btn-sm btn-outline var-flag-btn var-flag-btn--red',
              onClick: (entity: VAREntityEntry) => openFlagDialog(entity, 'redFlag'),
              isVisible: () => canFlagRed,
              isEnabled: () => canFlagRed,
            },
            {
              id: 'toggleSprinkler',
              label: 'Sprinkler',
              icon: '<i class="fa-solid fa-droplet"></i>',
              className: 'btn btn-sm btn-outline var-flag-btn var-flag-btn--sprinkler',
              onClick: (entity: VAREntityEntry) => openFlagDialog(entity, 'sprinkler'),
              isVisible: () => canFlagSprinkler,
              isEnabled: () => canFlagSprinkler,
            },
            {
              id: 'viewOnMap',
              label: 'View',
              icon: '<i class="fa-solid fa-map-location-dot"></i>',
              className: 'btn btn-sm btn-outline var-flag-btn var-flag-btn--map',
              onClick: (entity: VAREntityEntry) => handleViewOnMap(entity),
              isVisible: (entity: VAREntityEntry) => !!entity.location,
              isEnabled: (entity: VAREntityEntry) => !!entity.location,
            },
          ],
        },
      },
    };
  }, [openFlagDialog, handleViewOnMap, canFlagRed, canFlagSprinkler]);

  return (
    <div className={`var-explorer ${className || ''}`} style={style}>
      <ConfigurableBaseExplorer<VAREntityEntry>
        config={config}
        isAuthenticated={isLoggedIn}
        isWindowed={true}
        windowTitle="Values at Risk Explorer"
        help={<ContextHelp title='Help with Values at Risk' context='var-help' />}
        initialPosition={initialPosition || (window.innerWidth <= 768 ? { x: 10, y: 10 } : { x: 100, y: 100 })}
        initialSize={initialSize || { width, height }}
        initialSearchTerm={initialVarId}
        resizable={true}
        draggable={true}
        showCloseButton={true}
        showMinimizeButton={true}
        minimizeRef={minimizeRef}
        restoreRef={restoreRef}
        searchRef={searchRef}
        onWindowClose={onClose}
        onEntitySelect={onSelectionChange ? (entity) => onSelectionChange([entity]) : undefined}
        onEntityCreate={onDataChange}
        className="var-explorer-configured"
        testId="var-explorer"
        refreshTrigger={refreshTrigger}
      />
      {/* Mount the flag dialog only if the user holds the matching grant
          for the current flagType. Defence-in-depth alongside button gates. */}
      {((flagDialogType === 'redFlag' && canFlagRed) ||
        (flagDialogType === 'sprinkler' && canFlagSprinkler)) && (
        <VARFlagDialog
          isOpen={flagDialogOpen}
          onClose={() => setFlagDialogOpen(false)}
          entity={flagDialogEntity}
          flagType={flagDialogType}
          onSuccess={handleFlagSuccess}
        />
      )}
    </div>
  );
};

export default VARExplorer;
